import { useState, useEffect, useRef } from 'react';
import type { RouteConfig } from '@/types/route';
import { useLocale } from '@/hooks/useLocale';
import { useLocation } from 'react-router-dom';
import { useToast } from '@/components/Toast';
import type { Dayjs } from 'dayjs';
import { Table, Drawer, Input, DatePicker } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { Pagination } from "@/components/Pagination";
import type { RegistrationPage, RegistrationPageDetails, RegistrationPageImageMaterial } from '@/types/invitation';
import { MobileTableCard } from '@/components/MobileCards';
import dayjs from 'dayjs';
import InfoIcon from '../../../assets/svg/informationIcon.svg';
import { fetchCustomList } from '@/services/invitation';

export const routeConfig: RouteConfig = {
    title: 'route.invitationPromotion.customRegistrationPage',
    layout: true,
    sort: 1
};

const RegistrationPage = () => {
    const { t } = useLocale();
    const location = useLocation();
    const toast = useToast();

    const [loading, setLoading] = useState(false);
    const [registrationPageData, setRegistrationPageData] = useState<RegistrationPage[]>([]);
    // const [registrationPageDetailsData, setRegistrationPageDetailsData] = useState<RegistrationPageDetails[]>([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [total, setTotal] = useState(0);

    // For managing registrationPage drawer
    const [customOpen, setCustomOpen] = useState(false);
    const [isCreate, setIsCreate] = useState(false); // true for create, false for edit
    const [selectedRecord, setSelectedRecord] = useState<RegistrationPage | null>(null);
    const [imageMaterials, setImageMaterials] = useState<RegistrationPageImageMaterial[]>([]);

    const maxLen = 200; // 200 English or 100 Chinese

    // Helper to build params for invitation API
    const buildInvitationParams = () => {
        let params: any = {};
    //     if (code !== '') {
    //         params.invite_code = code;
    //     }
    //     if (selectedPromotionChannel !== 0) {
    //         params.channel_tag = selectedPromotionChannel;
    //     }
        return params;
    };

    // Helper to count "length" (Chinese = 2, English = 1)
    const getTextLength = (text: string) => {
        let len = 0;
        for (let i = 0; i < text.length; i++) {
            // Chinese unicode range
            if (/[\u4e00-\u9fa5]/.test(text[i])) {
                len += 2;
            } else {
                len += 1;
            }
        }
        return len;
    };

    const handleIntroductionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        let value = e.target.value;
        let len = getTextLength(value);
        // Truncate if over limit
        while (len > maxLen) {
            value = value.slice(0, -1);
            len = getTextLength(value);
        }
        setSelectedRecord(r => r ? { ...r, introduction: value } : r);
    };

    const introLen = getTextLength(selectedRecord?.profileInfo || '');

    const handleRegistrationClose = () => {
        setCustomOpen(false);
        setSelectedRecord(null);
    };

    const handleRegistrationSave = () => {
        // Save logic here
        if (selectedRecord) {
        }
        setCustomOpen(false);
        setSelectedRecord(null);
    };

    const handleCreate = () => {
        setSelectedRecord({
            name: '',
            isHidden: false,
            isLongTerm: true,
            default: false
        } as RegistrationPage);
        setIsCreate(true);
        setCustomOpen(true);
    };

    const handleEdit = (record: RegistrationPage) => {
        // Find the corresponding details
        if (record) {
            // Clone to avoid direct mutation
            setSelectedRecord({ ...record });
            setIsCreate(false);
            setCustomOpen(true);
        }
    };

    const handleUploadAvatar = () => {
        // Upload avatar logic here
        console.log('Uploading avatar...');
    };

    const handleUploadEvent = () => {
        // Upload event logic here
        console.log('Uploading event...');
    };

    const formatDate = (dateString?: string, time?: boolean) => {
        if (dateString === undefined || dateString === null || dateString === '') return t('invitationPromotion.invitationLink.none');
        if (dateString === '0') return '-';
        let date: dayjs.Dayjs;
        if (/^\d{10}$/.test(dateString)) {
            date = dayjs(Number(dateString) * 1000);
        } else {
            date = dayjs(dateString);
        }
        return time ? date.format('YYYY-MM-DD HH:mm:ss') : date.format('YYYY-MM-DD');
    };

    const registrationColumns: ColumnsType<RegistrationPage> = [
        {
            title: t('invitationPromotion.registration.table.name'),
            dataIndex: 'name',
            key: 'name',
            align: 'start',
            width: 150,
            ellipsis: true,
        },
        {
            title: t('invitationPromotion.registration.table.validity'),
            dataIndex: 'validity',
            key: 'validity',
            align: 'start',
            width: 150,
            ellipsis: true,
            render: (text) => <span className="break-all whitespace-normal"> {formatDate(text[0], false)} {t('invitationPromotion.registration.table.to')} {formatDate(text[1], false)} </span>
        },
        {
            title: t('invitationPromotion.registration.table.creationTime'),
            dataIndex: 'creationTime',
            key: 'creationTime',
            align: 'start',
            width: 150,
            ellipsis: true,
            render: (text) => formatDate(text)
        },
        // {
        //     title: t('invitationPromotion.registration.table.linkCount'),
        //     dataIndex: 'linkCount',
        //     key: 'linkCount',
        //     align: 'start',
        //     width: 140,
        //     ellipsis: true
        // },
        {
            title: t('invitationPromotion.registration.table.profileInfo'),
            dataIndex: 'profileInfo',
            key: 'profileInfo',
            align: 'start',
            width: 130,
            ellipsis: true,
            render: (text, record) => !record.isHidden ? text : 'Hidden'
        },
        {
            title: t('invitationPromotion.registration.table.status'),
            dataIndex: 'status',
            key: 'status',
            align: 'start',
            width: 130,
            ellipsis: true,
        },
        {
            title: t('invitationPromotion.registration.table.default'),
            dataIndex: 'default',
            key: 'default',
            align: 'start',
            width: 170,
            ellipsis: true,
            render: (isDefault) => isDefault ? t('invitationPromotion.registration.table.yes') : t('invitationPromotion.registration.table.no')
        },
        {
            title: t('invitationPromotion.registration.table.operation'),
            dataIndex: 'operation',
            key: 'operation',
            align: 'end',
            width: 110,
            fixed: 'right',
            render: (_, record) => (
                <div className="grid grid-cols-3 md:flex items-center justify-start md:justify-end gap-2">
                    <button
                        className="text-sm font-normal text-special-text font-inter w-14 bg-transparent border-none flex justify-center items-center"
                        onClick={() => handleEdit(record)}
                    >
                        {t('invitationPromotion.registration.table.edit')}
                    </button>
                </div>
            )
        }
    ];

    const mobileRegistrationColumns = registrationColumns
        .filter((col: any) => 'dataIndex' in col)
        .map((col: any) =>
            col.dataIndex === 'operation' ? { ...col, isOperation: true } : col
    );

    useEffect(() => {
        if (location.state?.customOpen) {
            setCustomOpen(true);
            console.log('Opening custom registration page drawer due to navigation state');
        }
    }, [location.state]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {

                // try {
                //     const groupRes = await fetchGroupID({});
                //     setCustomerGroups(groupRes);
                // } catch (groupError: any) {
                //     toast.error({
                //         message: groupError?.message || t('invitationPromotion.invitationLink.groupFetchError') || 'An error occurred while fetching group list.'
                //     });
                // }

                try {
                    const params = buildInvitationParams();
                    const res = await fetchCustomList(params, pagination.current, pagination.pageSize);
                    setRegistrationPageData(res.list);
                    setTotal(res.total);
                } catch (error: any) {
                    toast.error({
                        message: error?.message || t('invitationPromotion.registration.fetchError') || 'An error occurred while fetching client list.'
                    });
                }
            } catch (error: any) {
                toast.error({
                    message: error?.message || t('invitationPromotion.registration.fetchError') || 'An error occurred while fetching data.'
                });
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [pagination.current, pagination.pageSize]);

    return (
        <div className="bg-mainLayout dark:bg-dark-mainLayout min-h-screen px-6 pt-6 pb-40 md:pb-6">
            {customOpen ? (
                <>
                    <div className="flex items-center gap-[10px] text-sm font-normal font-inter leading-normal text-sel-normal dark:text-dark-sel-normal mb-4">
                        <span
                            className="cursor-pointer text-normal dark:text-dark-normal hover:text-sel-normal hover:dark:text-dark-sel-normal flex items-center text-sm font-normal font-inter leading-normal"
                            onClick={() => setCustomOpen(false)}
                        >
                            {t('invitationPromotion.registration.title')}
                        </span>
                        <svg className="w-4 h-4 text-normal dark:text-dark-normal" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                        <span className="text-sm font-normal font-inter leading-normal">
                            {isCreate ? t('invitationPromotion.registration.create') : t('invitationPromotion.registration.edit')}
                        </span>
                    </div>
                    <div className="flex gap-[10px] items-center pb-6">
                        <span className="text-xl font-normal font-inter leading-normal text-sel-normal dark:text-dark-sel-normal">
                            {t('invitationPromotion.registration.title')}
                        </span>
                    </div>
                    <div className="flex flex-row gap-6 h-full">
                        <div className="flex flex-col max-w-[428px] gap-6 h-full bg-card-background dark:bg-dark-card-background rounded-lg p-6">
                            {/* Page Name */}
                            <div>
                                <div className="flex items-start gap-[6px] mb-[6px]">
                                    <span className="text-place-order-bt-sell-text dark:text-dark-place-order-bt-sell-text text-sm font-normal font-inter leading-none">*</span>
                                    <span className="text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-none">{t('invitationPromotion.registration.custom.name')}</span>
                                </div>
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder={t('customerManagement.clientList.manage.clientLabelPlaceholder')}
                                        className="bg-card-background dark:bg-card-background border border-normal dark:border-dark-normal focus:ring-0 focus:outline-none rounded-lg px-3 py-[15px] w-full text-sel-normal dark:text-dark-sel-normal text-xs font-medium font-inter leading-none"
                                        maxLength={30}
                                        onChange={e => setSelectedRecord(r => r ? { ...r, name: e.target.value } : r)}
                                        value={selectedRecord?.name}
                                    />
                                </div>
                            </div>
                            {/* Hidden Settings */}
                            <div className="flex flex-col gap-4">
                                <div className="grid grid-cols-2 gap-2">
                                    <div className="gap-[6px] items-center">
                                        <div className="rounded flex justify-start items-center gap-2 cursor-pointer" 
                                            onClick={() => {
                                                setSelectedRecord(r => r ? { ...r, isHidden: !r.isHidden } : r);
                                        }}>
                                            <div className="flex w-5 h-5 relative justify-center items-center">
                                                <div
                                                    className={`w-4 h-4 border rounded flex items-center justify-center ${!selectedRecord?.isHidden ? 'border-tabline dark:border-dark-tabline' : 'border-underline dark:border-dark-underline'}`}
                                                >
                                                    {!selectedRecord?.isHidden && <div className='w-2 h-2 bg-tabline dark:bg-dark-tabline'></div>}
                                                </div>
                                            </div>
                                            <div className="justify-start text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-['Inter'] leading-tight">
                                                {t('invitationPromotion.registration.custom.showPersonalInfo')}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="gap-[6px] items-center">
                                        <div className="rounded flex justify-start items-center gap-2 cursor-pointer" 
                                            onClick={() => {
                                                setSelectedRecord(r => r ? { ...r, isHidden: !r.isHidden } : r);
                                        }}>
                                            <div className="flex w-5 h-5 relative justify-center items-center">
                                                <div
                                                    className={`w-4 h-4 border rounded flex items-center justify-center ${selectedRecord?.isHidden ? 'border-tabline dark:border-dark-tabline' : 'border-underline dark:border-dark-underline'}`}
                                                >
                                                    {selectedRecord?.isHidden && <div className='w-2 h-2 bg-tabline dark:bg-dark-tabline'></div>}
                                                </div>
                                            </div>
                                            <div className="justify-start text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-['Inter'] leading-tight">
                                                {t('invitationPromotion.registration.custom.hidePersonalInfo')}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-row bg-button dark:bg-dark-button rounded-lg p-4 gap-4">
                                    <div
                                        className="flex items-center justify-center text-center border border-normal dark:border-dark-normal rounded-lg"
                                        onClick={handleUploadAvatar}
                                    >
                                        {selectedRecord?.avatarImage ? (
                                            <img 
                                                src={selectedRecord.avatarImage} 
                                                alt="Avatar" 
                                                className="w-[104px] h-[104px] object-cover rounded-lg"
                                            />
                                        ) : (
                                            <div className="w-[104px] h-[104px] gap-2 flex flex-col items-center justify-center cursor-pointer">
                                                <svg className="w-4 h-4 text-normal dark:text-dark-normal" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                                                <span className="text-normal dark:text-dark-normal text-xs font-normal font-inter leading-none">
                                                    {t('invitationPromotion.registration.custom.upload')}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-1 min-w-0 items-center">
                                        <input
                                            type="text"
                                            placeholder={t('invitationPromotion.registration.custom.namePlaceholder')}
                                            className="bg-button dark:bg-dark-button border border-normal dark:border-dark-normal focus:ring-0 focus:outline-none rounded-lg px-3 py-[15px] w-full text-sel-normal dark:text-dark-sel-normal text-xs font-medium font-inter"
                                            maxLength={30}
                                            onChange={e => setSelectedRecord(r => r ? { ...r, username: e.target.value } : r)}
                                            value={selectedRecord?.username || ''}
                                        />
                                    </div>
                                </div>
                            </div>
                            {/* Introduction */}
                            <div>
                                <span className="flex mb-[6px] text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-none">
                                    {t('invitationPromotion.registration.custom.introduction')}
                                </span>
                                <div className="w-full h-[105px] flex flex-col gap-[6px] relative border border-normal dark:border-dark-normal rounded-lg p-3">
                                    <textarea
                                        placeholder={t('invitationPromotion.registration.custom.introductionPlaceholder')}
                                        className="mb-5 h-full bg-transparent focus:ring-0 focus:outline-none  w-full text-sel-normal dark:text-dark-sel-normal text-xs font-medium font-inter"
                                        maxLength={200}
                                        onChange={handleIntroductionChange}
                                        value={selectedRecord?.profileInfo || ''}
                                    />
                                    <span className="mt-2 absolute right-3 bottom-3 text-normal dark:text-dark-normal text-xs font-medium font-inter leading-tight select-none pointer-events-none">
                                        {introLen}/{maxLen}
                                    </span>
                                </div>
                            </div>
                            {/* Template */}
                            <div>
                                <div className="flex items-start gap-[6px] mb-[6px]">
                                    <span className="text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-none">{t('invitationPromotion.registration.custom.recommendedImageMaterials')}</span>
                                    <span>
                                        <img src={InfoIcon} alt="Information" className="w-4 h-4" />
                                    </span>
                                </div>
                                <div className="flex flex-col overflow-hidden w-full">
                                    <div className="flex flex-row gap-4 overflow-x-auto">
                                        {imageMaterials && imageMaterials.length > 0 ? (
                                            imageMaterials.map((img, idx) => (
                                                <div
                                                    key={img.id || idx}
                                                    className="w-[200px] h-[145px] border border-normal dark:border-dark-normal rounded-lg px-[6px] pt-[6px] pb-[10px] relative flex-shrink-0"
                                                >
                                                    <div className="flex items-center justify-center">
                                                        {img.previewImage ? (
                                                            <img
                                                                src={img.previewImage}
                                                                alt="Recommended"
                                                                className="w-full h-[102px] object-cover rounded"
                                                            />
                                                        ) : (
                                                            <div className="flex w-full h-[102px] bg-transparent dark:bg-dark-normal items-center justify-center text-normal text-xs">
                                                                {t('invitationPromotion.registration.custom.noImage')}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="absolute right-3 top-3 cursor-pointer">
                                                        <div
                                                            className="rounded flex justify-start items-center gap-2 cursor-pointer"
                                                            onClick={() => {
                                                                setSelectedRecord(r => r ? { ...r, templateId: img.id } : r);
                                                            }}
                                                        >
                                                            <div className="flex w-5 h-5 relative justify-center items-center">
                                                                <div
                                                                    className={`w-4 h-4 border rounded flex items-center justify-center ${
                                                                        selectedRecord?.templateId === img.id
                                                                            ? 'border-tabline dark:border-dark-tabline'
                                                                            : 'border-underline dark:border-dark-underline'
                                                                    }`}
                                                                >
                                                                    {selectedRecord?.templateId === img.id && (
                                                                        <div className="w-2 h-2 bg-tabline dark:bg-dark-tabline"></div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="items-end text-center mt-3 text-xs font-medium font-inter leading-tight text-normal dark:text-dark-normal">
                                                        { img.templateName ? img.templateName : t('invitationPromotion.registration.custom.default')}
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="w-[200px] h-[145px] flex items-center justify-center text-normal text-xs border border-dashed border-normal dark:border-dark-normal rounded-lg">
                                                {t('invitationPromotion.registration.custom.noImage')}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {/* Event Image */}
                            <div>
                                <div className="flex items-start gap-[6px] mb-[6px]">
                                    <span className="text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-none">{t('invitationPromotion.registration.custom.eventImage')}</span>
                                    <span className="text-normal dark:text-dark-normal text-sm font-normal font-inter leading-none">{t('invitationPromotion.registration.custom.recommendedSize')}</span>
                                    <span>
                                        <img src={InfoIcon} alt="Information" className="w-4 h-4" />
                                    </span>
                                </div>
                                <div
                                    className="flex w-[104px] h-[104px] items-center justify-center text-center border border-normal dark:border-dark-normal rounded-lg"
                                    onClick={handleUploadEvent}
                                >
                                    {selectedRecord?.eventImage ? (
                                        <img 
                                            src={selectedRecord.eventImage} 
                                            alt="Event" 
                                            className="w-[104px] h-[104px] object-cover rounded-lg"
                                        />
                                    ) : (
                                        <div className="w-[104px] h-[104px] gap-2 flex flex-col items-center justify-center cursor-pointer">
                                            <svg className="w-4 h-4 text-normal dark:text-dark-normal" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                                            <span className="text-normal dark:text-dark-normal text-xs font-normal font-inter leading-none">
                                                {t('invitationPromotion.registration.custom.upload')}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            {/* Validity and Event link */}
                            <div>
                                <div className="flex items-start gap-[6px] mb-[6px]">
                                    <span className="text-place-order-bt-sell-text dark:text-dark-place-order-bt-sell-text text-sm font-normal font-inter leading-none">*</span>
                                    <span className="text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-none">{t('invitationPromotion.registration.custom.validity')}</span>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div className="gap-[6px] items-center">
                                        <div className="rounded flex justify-start items-center gap-2 cursor-pointer" 
                                            onClick={() => {
                                                setSelectedRecord(r => r ? { ...r, isLongTerm: !r.isLongTerm } : r);
                                        }}>
                                            <div className="flex w-5 h-5 relative justify-center items-center">
                                                <div
                                                    className={`w-4 h-4 border rounded flex items-center justify-center ${selectedRecord?.isLongTerm ? 'border-tabline dark:border-dark-tabline' : 'border-underline dark:border-dark-underline'}`}
                                                >
                                                    {selectedRecord?.isLongTerm && <div className='w-2 h-2 bg-tabline dark:bg-dark-tabline'></div>}
                                                </div>
                                            </div>
                                            <div className="justify-start text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-['Inter'] leading-tight">
                                                {t('invitationPromotion.registration.custom.longTermEffective')}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="gap-[6px] items-center">
                                        <div className="rounded flex justify-start items-center gap-2 cursor-pointer" 
                                            onClick={() => {
                                                setSelectedRecord(r => r ? { ...r, isLongTerm: !r.isLongTerm } : r);
                                        }}>
                                            <div className="flex w-5 h-5 relative justify-center items-center">
                                                <div
                                                    className={`w-4 h-4 border rounded flex items-center justify-center ${!selectedRecord?.isLongTerm ? 'border-tabline dark:border-dark-tabline' : 'border-underline dark:border-dark-underline'}`}
                                                >
                                                    {!selectedRecord?.isLongTerm && <div className='w-2 h-2 bg-tabline dark:bg-dark-tabline'></div>}
                                                </div>
                                            </div>
                                            <div className="justify-start text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-['Inter'] leading-tight">
                                                {t('invitationPromotion.registration.custom.customTime')}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {/* Select Custom Time */}
                                {!selectedRecord?.isLongTerm && (
                                    <DatePicker.RangePicker
                                        id={`dateRange-custom-time`}
                                        className={`mt-[10px] h-8 w-full bg-card-background dark:bg-dark-card-background border border-normal dark:border-dark-normal rounded-md text-xs font-inter font-normal text-sel-normal dark:text-dark-sel-normal leading-none px-2 flex items-center focus:outline-none focus:ring-0 placeholder:text-normal dark:placeholder:text-dark-normal`}
                                        value={selectedRecord?.validity ? [dayjs(selectedRecord.validity[0]), dayjs(selectedRecord.validity[1])] : null}
                                        onChange={(dates) => {
                                            if (
                                                dates &&
                                                dates.length === 2 &&
                                                dates[0] !== null &&
                                                dates[1] !== null
                                            ) {
                                                const start = Math.floor(dates[0].valueOf() / 1000);
                                                const end = Math.floor(dates[1].valueOf() / 1000);
                                                setSelectedRecord(r => r ? { ...r, validity: [start.toString(), end.toString()] } : r);
                                            } else {
                                                setSelectedRecord(r => r ? { ...r, validity: undefined } : r);
                                            }
                                        }}
                                        placeholder={[t('invitationPromotion.registration.custom.start'), t('invitationPromotion.registration.custom.end')]}
                                        format={'YYYY-MM-DD'}
                                        showTime={false}
                                        allowClear={false}
                                        size={'middle'}
                                        placement={'bottomLeft'}
                                        getPopupContainer={() => document.body}
                                        style={{ width: '100%', height: '2rem', background: 'transparent', borderRadius: '0.375rem', fontSize: '0.75rem', fontFamily: 'Inter, sans-serif', color: 'inherit' }}
                                    />
                                )}

                                <div className="mt-[10px] flex items-start gap-[6px] mb-[6px]">
                                    <span className="text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-none">{t('invitationPromotion.registration.custom.eventLink')}</span>
                                </div>
                                <input
                                    type="text"
                                    placeholder={t('invitationPromotion.registration.custom.eventLinkPlaceholder')}
                                    className="bg-card-background dark:bg-dark-card-background border border-normal dark:border-dark-normal focus:ring-0 focus:outline-none rounded-lg px-3 py-[15px] w-full text-sel-normal dark:text-dark-sel-normal text-xs font-medium font-inter leading-none"
                                    maxLength={100}
                                    onChange={e => setSelectedRecord(r => r ? { ...r, eventLink: e.target.value ? parseInt(e.target.value) : undefined } : r)}
                                />
                            </div>
                            {/* Buttons */}
                            <div className="flex justify-end gap-2 pt-6 pb-6 px-6">
                                    <button
                                        onClick={handleRegistrationClose}
                                        className="bg-transparent border border-dark-main dark:border-main opacity-60 rounded-lg px-6 py-2 text-sel-normal dark:text-dark-sel-normal text-xs font-normal font-inter leading-tight cursor-pointer"
                                    >
                                        {t('invitationPromotion.registration.custom.cancel')}
                                    </button>
                                    <button
                                        onClick={() => {
                                            console.log('Save client management settings');
                                            handleRegistrationSave();
                                        }}
                                        className="bg-tabline border-none rounded-lg px-6 py-2 text-sel-normal dark:text-dark-sel-normal text-xs font-normal font-inter leading-tight cursor-pointer"
                                    >
                                        {isCreate ? t('invitationPromotion.registration.custom.create') : t('invitationPromotion.registration.custom.confirm')}
                                    </button>
                                </div>
                        </div>
                        <div className="flex-1 min-w-0 overflow-hidden">

                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div className="flex gap-[10px] items-center pb-6">
                        <span className="text-xl font-normal font-inter leading-normal text-sel-normal dark:text-dark-sel-normal">
                            {t('invitationPromotion.registration.title')}
                        </span>
                        <button 
                            className="bg-tabline dark:bg-dark-tabline hover:bg-hover-bg dark:hover:bg-dark-hover-bg text-sel-normal dark:text-dark-sel-normal text-xs font-normal font-inter leading-tight px-4 py-1.5 rounded whitespace-nowrap"
                            onClick={handleCreate}
                        >
                            {t('invitationPromotion.registration.create')}
                        </button>
                    </div>

                    {/* Table Container with Custom Layout */}
                    <div className="bg-card-background dark:bg-card-background rounded-lg p-6 overflow-hidden">
                        {/* Table Content */}
                        <div className="hidden md:block h-[556px] overflow-hidden overflow-y-auto overflow-x-auto">
                            <Table
                                dataSource={registrationPageData}
                                columns={registrationColumns}
                                rowKey="name"
                                pagination={false}
                                loading={loading}
                                size="small"
                                // scroll={{ x: 1000 }}
                                className="custom-action-table"
                                showHeader={true}
                                locale={{ emptyText: '' }}
                                components={{
                                    body: {
                                        wrapper: (props: any) => <tbody {...props} className="bg-card-background dark:bg-card-background" />,
                                    }
                                }}
                            />
                        </div>
                        <div className="block md:hidden">
                            {registrationPageData.map((row, idx) => (
                                <MobileTableCard
                                    key={row.name}
                                    record={row}
                                    columns={mobileRegistrationColumns}
                                    t={t}
                                />
                            ))}
                        </div>
                        <div className="mt-4 flex justify-end">
                            <Pagination
                                currentPage={pagination.current}
                                totalPages={Math.ceil(total / pagination.pageSize)}
                                onPageChange={(page: number) => setPagination({ ...pagination, current: page })}
                                pageSize={pagination.pageSize}
                                setPageSize={(size: number) => setPagination({ ...pagination, pageSize: size, current: 1 })}
                                pageSizes={[10, 20, 50, 100]}
                                isPageSizeVisible={true}
                                disabled={loading}
                            />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default RegistrationPage;